import { inclusiveEndDateFromExclusive } from './supplierFactorInterval';

// Shared seed-derivation for the two supplier surfaces that turn a declarant's
// data-request LINE into a starting point for the reused emissions modal:
// RequestDirectEntryPage (the full-page fallback, one line at a time via an
// explicit picker) and DataRequestsPage's inline "Provide data" flow (auto-
// advancing through every outstanding line without leaving the page). Both
// need the exact same seed/CN-good/queue-ordering rules — this module is
// their one shared implementation. Re-exported from supplierInboxPresentation.js
// for callers that only need the CTA/connection-level presentation surface.

// A request line's half-open interval → the inclusive date pair the operator
// emissions modal collects (date = startInclusive, lastRecordDate = endExclusive
// - 1 day). Seeds the request-tailored direct-entry publish so it targets the
// requested series (D-3 conversion, mirrored from the publish mapping).
//
// A5: also carries the line's product/method SCOPE, when the request named
// one, into the fields the reused modal maps onto seriesKey.productVariant /
// productionMethodCodes (see supplierFactorPayload.js seriesKeyFromModalData)
// — otherwise a scoped request's publish can miss its own fulfilment
// predicate (supplierDataRequestFulfilment.ts scopeMatches: productMatches /
// methodMatches). The backend normalizes productVariant on publish
// (normalizeSupplierProductVariant), so the raw string round-trips to the
// same comparison value as the request line without client-side normalizing.
export const requestLineToDirectEntrySeed = (line) => {
  const seed = { cnGood: line?.cnCode || '' };
  const start = line?.interval?.startInclusive;
  if (start) seed.date = String(start).slice(0, 10);
  const lastRecordDate = inclusiveEndDateFromExclusive(line?.interval?.endExclusive);
  if (lastRecordDate) seed.lastRecordDate = lastRecordDate;
  if (line?.product) seed.operatorGoodName = line.product;
  const methodCodes = String(line?.method || '')
    .split(/[|,]/)
    .map((code) => code.trim())
    .filter(Boolean);
  if (methodCodes.length > 0) {
    seed.productionMethods = methodCodes.map((code) => ({
      methodCode: code,
      name: code,
      directParameters: [],
    }));
  }
  return seed;
};

// Q3 (S7 fix batch 3): a request line's CN code can be a full catalog code
// (matches exactly one CnGood — CnGoodField's equality check is
// `_id === value || cnCode === value`, so a full code resolves cleanly) or a
// CHAPTER/HEADING-level code the declarant used to describe the goods loosely
// (e.g. "7208", a prefix of many specific catalog codes like "72085120").
// Only the former is safe to preselect: auto-picking one of several
// prefix-matches would silently guess which specific good the supplier
// produces.
export const RequestLineCnGoodResolution = Object.freeze({
  Matched: 'matched',
  Ambiguous: 'ambiguous',
  NoMatch: 'no_match',
});

const normalizedCnCode = (value) => String(value || '').replace(/\s+/g, '');

export const resolveRequestLineCnGood = (cnCode, cnGoods) => {
  const requested = normalizedCnCode(cnCode);
  if (!requested) return { state: RequestLineCnGoodResolution.NoMatch, good: null };
  const goods = cnGoods || [];
  const codeOf = (g) => normalizedCnCode(g?.cnCode || g?._id);
  // An exact match wins outright, even if OTHER catalog entries also happen
  // to start with the same prefix — the declarant asked for exactly this code.
  const exact = goods.find((g) => codeOf(g) === requested);
  if (exact) return { state: RequestLineCnGoodResolution.Matched, good: exact };
  const prefixMatches = goods.filter((g) => codeOf(g).startsWith(requested));
  if (prefixMatches.length === 1) {
    return { state: RequestLineCnGoodResolution.Matched, good: prefixMatches[0] };
  }
  if (prefixMatches.length > 1) {
    return { state: RequestLineCnGoodResolution.Ambiguous, good: null };
  }
  return { state: RequestLineCnGoodResolution.NoMatch, good: null };
};

// A4: the index of the first line the supplier still owes data for. Defaults
// multi-line direct entry to the actionable line instead of whichever line
// happens to be first; falls back to the first line once every line is
// already fulfilled (nothing left to prioritize).
export const defaultDirectEntryLineIndex = (lines) => {
  const idx = (lines || []).findIndex((line) => line?.fulfilment?.state !== 'fulfilled');
  return idx === -1 ? 0 : idx;
};

// Combines requestLineToDirectEntrySeed with the CN-good catalog resolution
// (Q3): a full catalog code preselects cleanly; a chapter/heading-level code
// is a PREFIX many specific catalog goods can share, so it is never
// auto-picked — `needsCnGoodPick` tells the caller to show a hint instead.
// The hint TEXT itself needs a display label (e.g. useCnGoodLabel), which
// lives in the app layer, so it is composed by the caller, not here.
export const resolveRequestLineEntrySeed = (line, cnGoods) => {
  if (!line) return { seed: null, cnResolution: null, needsCnGoodPick: false };
  const rawSeed = requestLineToDirectEntrySeed(line);
  const cnResolution = resolveRequestLineCnGood(line.cnCode, cnGoods);
  const matched = cnResolution.state === RequestLineCnGoodResolution.Matched;
  const seed = matched
    ? { ...rawSeed, cnGood: cnResolution.good.cnCode || cnResolution.good._id }
    : rawSeed;
  return { seed, cnResolution, needsCnGoodPick: !matched };
};

// The DataRequestsPage inline "Provide data" flow walks every line the
// request still needs data for, in original order. The queue is a SNAPSHOT
// taken once when the flow opens — it does not shrink as lines are saved
// (a draft save never changes fulfilment by product decision, and a
// publish's fulfilment flip is only reflected once the caller refetches the
// inbox at the END of the whole sequence) — so "N of M" stays stable and
// predictable across the walk. `index` is the line's position in the
// request's own `lines` array, kept alongside for callers that need it
// (e.g. resolving the SAME line again after a save).
export const buildRequestEntryQueue = (lines) =>
  (lines || [])
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => line?.fulfilment?.state !== 'fulfilled');

// "N of M" progress copy for the modal — position is 0-based.
export const requestEntryProgressLabel = (position, total) => `${position + 1} of ${total}`;

// The honest completion summary once the whole queue has been walked (item
// B/3): a request is only fulfilled by PUBLISHED factors, so a line saved as
// a draft — or skipped — must NOT be reported as "provided". Pure so the
// three possible outcomes (all published / a mix / all drafted or skipped)
// are each independently testable without driving the actual modal flow.
export const requestEntryCompletionMessage = ({
  total,
  publishedCount = 0,
  draftCount = 0,
  skippedCount = 0,
}) => {
  const noun = total === 1 ? 'product' : 'products';
  if (publishedCount === total) return `All ${total} ${noun} provided.`;
  const parts = [`${publishedCount} of ${total} ${noun} provided`];
  if (draftCount > 0) {
    parts.push(
      `${draftCount} saved as ${draftCount === 1 ? 'a draft' : 'drafts'} — publish ${
        draftCount === 1 ? 'it' : 'them'
      } from Review drafts to fulfil`,
    );
  }
  if (skippedCount > 0) parts.push(`${skippedCount} skipped`);
  return `${parts.join('; ')}.`;
};
